import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import OptiBuildLogo from './OptibuildLogo';
import { Eye, EyeOff, Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { authService } from '../src/services/auth.service';

/**
 * mode='set'   — new user after email verification; token = set_password_token from validate response.
 *               On success backend returns AuthTokens → user lands on dashboard.
 * mode='reset' — existing user via forgot-password email link; token = URL ?token= param.
 *               On success backend returns { message } → user goes to login.
 */
interface SetPasswordPageProps {
  mode: 'set' | 'reset';
  token: string;
  onSuccess: () => void;       // called after password set/reset
  onBackToLogin: () => void;
}

interface StrengthResult {
  score: number;    // 0-4
  label: string;
  color: string;
}

function getStrength(pw: string): StrengthResult {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;

  const levels: StrengthResult[] = [
    { score: 0, label: '', color: '' },
    { score: 1, label: 'Weak', color: 'bg-red-500' },
    { score: 2, label: 'Fair', color: 'bg-yellow-500' },
    { score: 3, label: 'Good', color: 'bg-blue-500' },
    { score: 4, label: 'Strong', color: 'bg-green-500' },
  ];
  return levels[score];
}

export function SetPasswordPage({ mode, token, onSuccess, onBackToLogin }: SetPasswordPageProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const strength = getStrength(password);
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= 8 && password === confirm && !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsLoading(true);
    setError('');

    try {
      if (mode === 'set') {
        const tokens = await authService.setPassword(token, password);
        // Tokens stored in authService.storeTokens; auth state will update and onSuccess
        // navigates to dashboard via App.tsx auth-state effect.
        onSuccess();
      } else {
        await authService.resetPassword(token, password);
        setDone(true);
        setTimeout(onSuccess, 3000);
      }
    } catch (err: any) {
      const knownErrors: Record<string, string> = {
        invalid_token: 'This link has expired or already been used. Please request a new one.',
        token_expired: 'This link has expired. Please request a new one.',
        weak_password: 'Password does not meet security requirements.',
      };
      setError(knownErrors[err.error] ?? err.message ?? 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const title = mode === 'set' ? 'Set your password' : 'Choose a new password';
  const subtitle =
    mode === 'set'
      ? 'Create a secure password to complete your account setup.'
      : 'Enter a new password for your OptiBuild account.';
  const cta = mode === 'set' ? 'Set Password & Sign In' : 'Reset Password';

  if (done) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <OptiBuildLogo size="lg" variant="full" className="mx-auto mb-6" />
          </div>
          <Card>
            <CardContent className="p-8 space-y-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-2">Password reset!</h2>
                <p className="text-muted-foreground">
                  Your password has been updated. Redirecting you to sign in…
                </p>
              </div>
              <Button className="w-full" onClick={onSuccess}>
                Sign In Now
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <OptiBuildLogo size="lg" variant="full" className="mx-auto mb-6" />
        </div>

        <Card>
          <CardContent className="p-8 space-y-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">{title}</h2>
              <p className="text-muted-foreground mt-1">{subtitle}</p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPw ? 'text' : 'password'}
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    autoFocus
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {/* Strength bar */}
                {password.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full transition-colors ${
                            i <= strength.score ? strength.color : 'bg-muted'
                          }`}
                        />
                      ))}
                    </div>
                    {strength.label && (
                      <p className="text-xs text-muted-foreground">{strength.label} password</p>
                    )}
                  </div>
                )}

                <ul className="text-xs text-muted-foreground space-y-0.5">
                  <li className={password.length >= 8 ? 'text-green-600' : ''}>
                    {password.length >= 8 ? '✓' : '·'} At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(password) ? 'text-green-600' : ''}>
                    {/[A-Z]/.test(password) ? '✓' : '·'} One uppercase letter
                  </li>
                  <li className={/[0-9]/.test(password) ? 'text-green-600' : ''}>
                    {/[0-9]/.test(password) ? '✓' : '·'} One number
                  </li>
                  <li className={/[^A-Za-z0-9]/.test(password) ? 'text-green-600' : ''}>
                    {/[^A-Za-z0-9]/.test(password) ? '✓' : '·'} One special character
                  </li>
                </ul>
              </div>

              {/* Confirm password */}
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirm"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Re-enter your password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                    disabled={isLoading}
                    className={`pr-10 ${mismatch ? 'border-destructive' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {mismatch && (
                  <p className="text-xs text-destructive">Passwords do not match.</p>
                )}
              </div>

              <Button className="w-full" onClick={handleSubmit} disabled={!canSubmit}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  cta
                )}
              </Button>

              <Button variant="ghost" className="w-full" onClick={onBackToLogin}>
                Back to Sign In
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
